import React, {ReactElement} from "react";
import {
    AppstoreOutlined,
    AuditOutlined,
    BarcodeOutlined,
    CalculatorOutlined,
    CalendarOutlined,
    ClusterOutlined,
    CloudUploadOutlined,
    ContactsOutlined,
    DashboardOutlined,
    DatabaseOutlined,
    DeploymentUnitOutlined,
    ExperimentOutlined,
    InboxOutlined,
    SettingOutlined,
    ShoppingCartOutlined,
    SwapOutlined,
    TagsOutlined,
    TeamOutlined
} from "@ant-design/icons";
import {ApplicationUserRole} from "../api";
import {
    MANAGE_CONTRACTORS_ROLES,
    MANAGE_INVENTORY_ROLES,
    MANAGE_ORDER_ROLES,
    MANAGE_OPERATIONS_ROLES,
    MANAGE_PRODUCTS_ROLES,
    MANAGE_PRODUCTION_ROLES,
    MANAGE_SETTINGS_ROLES,
    MANAGE_USERS_ROLES,
    MANAGE_WAREHOUSES_ROLES,
    READ_CONTRACTORS_ROLES,
    READ_INVENTORY_ROLES,
    READ_OPERATIONS_ROLES,
    READ_PRODUCTS_ROLES,
    READ_PRODUCTION_ROLES,
    READ_STOCK_ROLES,
    READ_WAREHOUSES_ROLES
} from "../helpers/applicationUserHelper";
import {RoutingPaths} from "../pages/routing/routingContainer";

export type NavigationSectionKey =
    | "start"
    | "warehouse"
    | "production"
    | "relationships"
    | "shop"
    | "admin";

export interface NavigationSectionDefinition {
    key: NavigationSectionKey;
    label: string;
    color: string;
}

export interface NavigationItemDefinition {
    key: string;
    displayName: string;
    description: string;
    route: string;
    icon: ReactElement;
    section: NavigationSectionKey;
    allowedForRoles?: ApplicationUserRole[];
    mobilePlacement?: "upper" | "main" | "lower";
}

interface RouteActionDefinition {
    label: string;
    route: string;
    allowedForRoles?: ApplicationUserRole[];
}

export interface RouteContextDefinition {
    routePattern: string;
    title: string;
    description: string;
    section: NavigationSectionKey;
    navRoute?: string;
    actions?: RouteActionDefinition[];
}

const link = (
    label: string,
    route: string,
    allowedForRoles?: ApplicationUserRole[]
): RouteActionDefinition => ({
    label,
    route,
    allowedForRoles
});

const context = (
    routePattern: string,
    title: string,
    description: string,
    section: NavigationSectionKey,
    navRoute?: string,
    actions?: RouteActionDefinition[]
): RouteContextDefinition => ({
    routePattern,
    title,
    description,
    section,
    navRoute,
    actions
});

export const navigationSections: NavigationSectionDefinition[] = [
    {key: "start", label: "Start i przegląd", color: "#7b5b30"},
    {key: "warehouse", label: "Magazyn", color: "#385c6b"},
    {key: "production", label: "Produkcja", color: "#556b2f"},
    {key: "relationships", label: "Relacje", color: "#824d36"},
    {key: "shop", label: "Sklep", color: "#8b6a3d"},
    {key: "admin", label: "Administracja", color: "#5b5f77"}
];

export const navigationItems: NavigationItemDefinition[] = [
    {
        key: "home",
        displayName: "Strona główna",
        description: "Panel startowy z najważniejszymi informacjami i skrótami.",
        route: RoutingPaths.mainPage.route,
        icon: <DashboardOutlined/>,
        section: "start",
        mobilePlacement: "upper"
    },
    {
        key: "calendar",
        displayName: "Kalendarz",
        description: "Widok terminów i zaplanowanych działań.",
        route: RoutingPaths.warehouseOperationsCalendar.route,
        icon: <CalendarOutlined/>,
        section: "start",
        allowedForRoles: READ_OPERATIONS_ROLES,
        mobilePlacement: "upper"
    },
    {
        key: "general-stock",
        displayName: "Stany magazynowe",
        description: "Szybki podgląd stanów produktów i partii.",
        route: RoutingPaths.stockGeneralTable.route,
        icon: <InboxOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_STOCK_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "warehouses",
        displayName: "Magazyny",
        description: "Struktura magazynów, lokalizacje i etykiety.",
        route: RoutingPaths.warehouseTable.route,
        icon: <DatabaseOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_WAREHOUSES_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "products",
        displayName: "Produkty",
        description: "Katalog produktów, ich parametry i stany.",
        route: RoutingPaths.productTable.route,
        icon: <BarcodeOutlined/>,
        section: "warehouse",
        allowedForRoles: [],
        mobilePlacement: "main"
    },
    {
        key: "stock-operations",
        displayName: "Operacje magazynowe",
        description: "PZ, WZ, MM, PW, RW oraz historia zmian.",
        route: RoutingPaths.stockOperationTable.route,
        icon: <SwapOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_OPERATIONS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "inventory-operations",
        displayName: "Inwentaryzacje",
        description: "Spisy i publikacja wyników.",
        route: RoutingPaths.inventoryOperationTable.route,
        icon: <AuditOutlined/>,
        section: "warehouse",
        allowedForRoles: READ_INVENTORY_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "production-processes",
        displayName: "Procesy produkcyjne",
        description: "Nadzór nad etapami produkcji i badaniami procesu.",
        route: RoutingPaths.productionProcessTable.route,
        icon: <ExperimentOutlined/>,
        section: "production",
        allowedForRoles: READ_PRODUCTION_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "crops",
        displayName: "Uprawy",
        description: "Lista upraw i przejście do ich szczegółów.",
        route: RoutingPaths.cropTable.route,
        icon: <ClusterOutlined/>,
        section: "production",
        allowedForRoles: READ_PRODUCTION_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "strains",
        displayName: "Szczepy",
        description: "Szczepy używane w produktach i procesach.",
        route: RoutingPaths.strainTable.route,
        icon: <DeploymentUnitOutlined/>,
        section: "production",
        allowedForRoles: MANAGE_PRODUCTS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "contractors",
        displayName: "Kontrahenci",
        description: "Relacje handlowe, spotkania i historia operacji.",
        route: RoutingPaths.contractorTable.route,
        icon: <ContactsOutlined/>,
        section: "relationships",
        allowedForRoles: READ_CONTRACTORS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "shop-categories",
        displayName: "Kategorie sklepu",
        description: "Struktura kategorii i powiązań z produktami sklepowymi.",
        route: RoutingPaths.shopCategoryTable.route,
        icon: <AppstoreOutlined/>,
        section: "shop",
        allowedForRoles: READ_PRODUCTS_ROLES,
        mobilePlacement: "main"
    },
    {
        key: "shop-discount-codes",
        displayName: "Kody rabatowe",
        description: "Promocje, limity użyć i zakres kodów rabatowych.",
        route: RoutingPaths.shopDiscountCodeTable.route,
        icon: <TagsOutlined/>,
        section: "shop",
        allowedForRoles: MANAGE_ORDER_ROLES,
        mobilePlacement: "lower"
    },
    {
        key: "shop-baskets",
        displayName: "Koszyki sklepu",
        description: "Podgląd aktywnych koszyków i ich zawartości.",
        route: RoutingPaths.shopBasketTable.route,
        icon: <ShoppingCartOutlined/>,
        section: "shop",
        allowedForRoles: MANAGE_ORDER_ROLES,
        mobilePlacement: "lower"
    },
    {
        key: "shop-orders",
        displayName: "Zamówienia sklepu",
        description: "Lista zamówień ze statusami, płatnościami i dostawą.",
        route: RoutingPaths.shopOrderTable.route,
        icon: <ShoppingCartOutlined/>,
        section: "shop",
        allowedForRoles: MANAGE_ORDER_ROLES,
        mobilePlacement: "lower"
    },
    {
        key: "users",
        displayName: "Użytkownicy",
        description: "Zarządzanie użytkownikami i ich historią zmian.",
        route: RoutingPaths.userTable.route,
        icon: <TeamOutlined/>,
        section: "admin",
        allowedForRoles: MANAGE_USERS_ROLES,
        mobilePlacement: "lower"
    },
    {
        key: "deploy",
        displayName: "Publikacja",
        description: "Podgląd projektów Cloudflare i statusów publikacji sklepu.",
        route: RoutingPaths.deploy.route,
        icon: <CloudUploadOutlined/>,
        section: "admin",
        allowedForRoles: [ApplicationUserRole.Admin],
        mobilePlacement: "lower"
    },
    {
        key: "units",
        displayName: "Jednostki miary",
        description: "Jednostki bazowe i pomocnicze używane w systemie.",
        route: RoutingPaths.unitOfMeasureTable.route,
        icon: <CalculatorOutlined/>,
        section: "admin",
        allowedForRoles: MANAGE_PRODUCTS_ROLES,
        mobilePlacement: "lower"
    },
    {
        key: "company-data",
        displayName: "Dane firmy",
        description: "Dane organizacji, ustawienia i historia zmian.",
        route: RoutingPaths.companyData.route,
        icon: <SettingOutlined/>,
        section: "admin",
        allowedForRoles: MANAGE_SETTINGS_ROLES,
        mobilePlacement: "lower"
    }
];

export const routeContexts: RouteContextDefinition[] = [
    context(
        RoutingPaths.mainPage.route,
        "Strona główna",
        "Najważniejsze informacje i szybkie przejścia do modułów.",
        "start",
        RoutingPaths.mainPage.route,
        [
            link("Stany magazynowe", RoutingPaths.stockGeneralTable.route, READ_STOCK_ROLES),
            link("Procesy produkcyjne", RoutingPaths.productionProcessTable.route, READ_PRODUCTION_ROLES),
            link("Kontrahenci", RoutingPaths.contractorTable.route, READ_CONTRACTORS_ROLES)
        ]
    ),
    context(
        RoutingPaths.warehouseOperationsCalendar.route,
        "Kalendarz",
        "Widok terminów i zaplanowanych działań.",
        "start",
        RoutingPaths.warehouseOperationsCalendar.route
    ),
    context(
        RoutingPaths.stockGeneralTable.route,
        "Stany magazynowe",
        "Podgląd stanów produktów i partii.",
        "warehouse",
        RoutingPaths.stockGeneralTable.route,
        [
            link("Magazyny", RoutingPaths.warehouseTable.route, READ_WAREHOUSES_ROLES),
            link("Produkty", RoutingPaths.productTable.route, READ_PRODUCTS_ROLES)
        ]
    ),
    context(
        RoutingPaths.warehouseTable.route,
        "Magazyny",
        "Lista magazynów i struktury lokalizacji.",
        "warehouse",
        RoutingPaths.warehouseTable.route,
        [link("Dodaj magazyn", RoutingPaths.warehouseAdd.route, MANAGE_WAREHOUSES_ROLES)]
    ),
    context(
        RoutingPaths.subWarehouseTable.route,
        "Magazyny podrzędne",
        "Widok zawężony do wybranego fragmentu struktury magazynowej.",
        "warehouse",
        RoutingPaths.warehouseTable.route,
        [link("Lista magazynów", RoutingPaths.warehouseTable.route, READ_WAREHOUSES_ROLES)]
    ),
    context(
        RoutingPaths.warehouseAdd.route,
        "Nowy magazyn",
        "Formularz tworzenia magazynu.",
        "warehouse",
        RoutingPaths.warehouseTable.route,
        [link("Lista magazynów", RoutingPaths.warehouseTable.route, READ_WAREHOUSES_ROLES)]
    ),
    context(
        RoutingPaths.subWarehouseAdd.route,
        "Nowy magazyn podrzędny",
        "Dodawanie podrzędnej lokalizacji w strukturze magazynowej.",
        "warehouse",
        RoutingPaths.warehouseTable.route,
        [link("Lista magazynów", RoutingPaths.warehouseTable.route, READ_WAREHOUSES_ROLES)]
    ),
    context(
        RoutingPaths.warehouseEdit.route,
        "Edycja magazynu",
        "Zmiana danych wybranego magazynu.",
        "warehouse",
        RoutingPaths.warehouseTable.route,
        [link("Lista magazynów", RoutingPaths.warehouseTable.route, READ_WAREHOUSES_ROLES)]
    ),
    context(
        RoutingPaths.warehouseDetails.route,
        "Szczegóły magazynu",
        "Szczegóły i historia zmian wybranego magazynu.",
        "warehouse",
        RoutingPaths.warehouseTable.route,
        [link("Lista magazynów", RoutingPaths.warehouseTable.route, READ_WAREHOUSES_ROLES)]
    ),
    context(
        RoutingPaths.productTable.route,
        "Produkty",
        "Katalog produktów z szybkim przejściem do stanów i historii.",
        "warehouse",
        RoutingPaths.productTable.route,
        [link("Dodaj produkt", RoutingPaths.productAdd.route, MANAGE_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.productAdd.route,
        "Nowy produkt",
        "Formularz tworzenia produktu.",
        "warehouse",
        RoutingPaths.productTable.route,
        [link("Lista produktów", RoutingPaths.productTable.route, READ_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.productEdit.route,
        "Edycja produktu",
        "Zmiana danych produktu.",
        "warehouse",
        RoutingPaths.productTable.route,
        [link("Lista produktów", RoutingPaths.productTable.route, READ_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.productUpdateHistory.route,
        "Historia produktu",
        "Historia zmian wybranego produktu.",
        "warehouse",
        RoutingPaths.productTable.route,
        [link("Lista produktów", RoutingPaths.productTable.route, READ_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.productStockTable.route,
        "Stany produktu",
        "Widok stanów dla wybranego produktu.",
        "warehouse",
        RoutingPaths.productTable.route
    ),
    context(
        RoutingPaths.productBatchStockTable.route,
        "Partia produktu",
        "Szczegóły partii wybranego produktu.",
        "warehouse",
        RoutingPaths.productTable.route
    ),
    context(
        RoutingPaths.productBatchUnitStockScan.route,
        "Jednostka partii",
        "Widok jednostki w ramach wybranej partii produktu.",
        "warehouse",
        RoutingPaths.productTable.route
    ),
    context(
        RoutingPaths.productStockHistory.route,
        "Historia stanu produktu",
        "Historia zmian stanu produktu w magazynie.",
        "warehouse",
        RoutingPaths.productTable.route
    ),
    context(
        RoutingPaths.warehouseStockTable.route,
        "Stan magazynu",
        "Widok stan\u00f3w dla konkretnego magazynu.",
        "warehouse",
        RoutingPaths.warehouseTable.route
    ),
    context(
        RoutingPaths.stockReservationHistory.route,
        "Historia rezerwacji",
        "Historia zmian rezerwacji dla produktu i magazynu.",
        "warehouse",
        RoutingPaths.stockGeneralTable.route
    ),
    context(
        RoutingPaths.stockOperationTable.route,
        "Operacje magazynowe",
        "Lista ruch\u00f3w magazynowych i ich status\u00f3w.",
        "warehouse",
        RoutingPaths.stockOperationTable.route,
        [
            link("Dodaj operacj\u0119", RoutingPaths.stockOperationAddNoParams.route, MANAGE_OPERATIONS_ROLES),
            link("Dodaj operacj\u0119 produkcyjn\u0105", RoutingPaths.productionStockOperationAddNoParams.route, MANAGE_PRODUCTION_ROLES)
        ]
    ),
    context(
        RoutingPaths.stockOperationAdd.route,
        "Nowa operacja magazynowa",
        "Formularz operacji magazynowej.",
        "warehouse",
        RoutingPaths.stockOperationTable.route,
        [link("Lista operacji", RoutingPaths.stockOperationTable.route, READ_OPERATIONS_ROLES)]
    ),
    context(
        RoutingPaths.stockOperationProcessAdd.route,
        "Nowa operacja dla procesu",
        "Dodawanie operacji magazynowej w kontek\u015bcie procesu produkcyjnego.",
        "warehouse",
        RoutingPaths.stockOperationTable.route,
        [link("Lista operacji", RoutingPaths.stockOperationTable.route, READ_OPERATIONS_ROLES)]
    ),
    context(
        RoutingPaths.stockOperationAddNoParams.route,
        "Nowa operacja magazynowa",
        "Formularz operacji magazynowej bez wst\u0119pnie wybranego kontekstu.",
        "warehouse",
        RoutingPaths.stockOperationTable.route,
        [link("Lista operacji", RoutingPaths.stockOperationTable.route, READ_OPERATIONS_ROLES)]
    ),
    context(
        RoutingPaths.stockOperationEdit.route,
        "Edycja operacji magazynowej",
        "Poprawa danych operacji magazynowej.",
        "warehouse",
        RoutingPaths.stockOperationTable.route,
        [link("Lista operacji", RoutingPaths.stockOperationTable.route, READ_OPERATIONS_ROLES)]
    ),
    context(
        RoutingPaths.stockOperationDetailsTable.route,
        "Szczegóły operacji magazynowej",
        "Szczegóły jednej operacji magazynowej.",
        "warehouse",
        RoutingPaths.stockOperationTable.route
    ),
    context(
        RoutingPaths.stockOperationHistory.route,
        "Historia operacji magazynowej",
        "Historia zmian wybranej operacji magazynowej.",
        "warehouse",
        RoutingPaths.stockOperationTable.route
    ),
    context(
        RoutingPaths.internalStockOperationDetailsTable.route,
        "Szczegóły operacji wewnętrznej",
        "Widok szczegółów operacji wewnętrznej.",
        "warehouse",
        RoutingPaths.stockOperationTable.route
    ),
    context(
        RoutingPaths.inventoryOperationTable.route,
        "Inwentaryzacje",
        "Lista spisów i publikacji wyników.",
        "warehouse",
        RoutingPaths.inventoryOperationTable.route,
        [link("Dodaj inwentaryzację", RoutingPaths.inventoryOperationAdd.route, MANAGE_INVENTORY_ROLES)]
    ),
    context(
        RoutingPaths.inventoryOperationAdd.route,
        "Nowa inwentaryzacja",
        "Formularz nowej inwentaryzacji.",
        "warehouse",
        RoutingPaths.inventoryOperationTable.route,
        [link("Lista inwentaryzacji", RoutingPaths.inventoryOperationTable.route, READ_INVENTORY_ROLES)]
    ),
    context(
        RoutingPaths.inventoryOperationEdit.route,
        "Edycja inwentaryzacji",
        "Zmiana danych wybranej inwentaryzacji.",
        "warehouse",
        RoutingPaths.inventoryOperationTable.route,
        [link("Lista inwentaryzacji", RoutingPaths.inventoryOperationTable.route, READ_INVENTORY_ROLES)]
    ),
    context(
        RoutingPaths.productionProcessTable.route,
        "Procesy produkcyjne",
        "Główne miejsce do śledzenia etapów produkcji i badań.",
        "production",
        RoutingPaths.productionProcessTable.route,
        [
            link("Dodaj proces", RoutingPaths.productionProcessAdd.route, MANAGE_PRODUCTION_ROLES),
            link("Dodaj operację produkcyjną", RoutingPaths.productionStockOperationAddNoParams.route, MANAGE_PRODUCTION_ROLES)
        ]
    ),
    context(
        RoutingPaths.productionProcessAdd.route,
        "Nowy proces produkcyjny",
        "Formularz nowego procesu produkcyjnego.",
        "production",
        RoutingPaths.productionProcessTable.route,
        [link("Lista procesów", RoutingPaths.productionProcessTable.route, READ_PRODUCTION_ROLES)]
    ),
    context(
        RoutingPaths.productionProcessEdit.route,
        "Edycja procesu produkcyjnego",
        "Zmiana danych procesu produkcyjnego.",
        "production",
        RoutingPaths.productionProcessTable.route,
        [link("Lista proces\u00f3w", RoutingPaths.productionProcessTable.route, READ_PRODUCTION_ROLES)]
    ),
    context(
        RoutingPaths.processStockOperationDetails.route,
        "Szczegóły procesu",
        "Widok szczegółów procesu, etapów i operacji.",
        "production",
        RoutingPaths.productionProcessTable.route
    ),
    context(
        RoutingPaths.productionStockOperationAddNoParams.route,
        "Nowa operacja produkcyjna",
        "Formularz operacji produkcyjnej.",
        "production",
        RoutingPaths.productionProcessTable.route,
        [link("Lista proces\u00f3w", RoutingPaths.productionProcessTable.route, READ_PRODUCTION_ROLES)]
    ),
    context(
        RoutingPaths.productionStockOperationEdit.route,
        "Edycja operacji produkcyjnej",
        "Zmiana danych operacji produkcyjnej.",
        "production",
        RoutingPaths.productionProcessTable.route
    ),
    context(
        RoutingPaths.productionStockOperationDetailsTable.route,
        "Szczegóły operacji produkcyjnej",
        "Szczegóły wybranej operacji produkcyjnej.",
        "production",
        RoutingPaths.productionProcessTable.route
    ),
    context(
        RoutingPaths.vinificationStockOperationForm.route,
        "Etap winifikacji",
        "Formularz etapu winifikacji powiązanego z procesem produkcyjnym.",
        "production",
        RoutingPaths.productionProcessTable.route
    ),
    context(
        RoutingPaths.physicochemicalTestTable.route,
        "Badania fizykochemiczne",
        "Lista badań przypisanych do procesu produkcyjnego.",
        "production",
        RoutingPaths.productionProcessTable.route
    ),
    context(
        RoutingPaths.physicochemicalTestAdd.route,
        "Nowe badanie fizykochemiczne",
        "Formularz nowego badania dla procesu produkcyjnego.",
        "production",
        RoutingPaths.productionProcessTable.route
    ),
    context(
        RoutingPaths.physicochemicalTestEdit.route,
        "Edycja badania fizykochemicznego",
        "Zmiana danych badania procesu produkcyjnego.",
        "production",
        RoutingPaths.productionProcessTable.route
    ),
    context(
        RoutingPaths.cropTable.route,
        "Uprawy",
        "Lista upraw z szybkim przejściem do szczegółów.",
        "production",
        RoutingPaths.cropTable.route,
        [link("Dodaj uprawę", RoutingPaths.cropAdd.route, MANAGE_PRODUCTION_ROLES)]
    ),
    context(
        RoutingPaths.cropAdd.route,
        "Nowa uprawa",
        "Formularz nowej uprawy.",
        "production",
        RoutingPaths.cropTable.route,
        [link("Lista upraw", RoutingPaths.cropTable.route, READ_PRODUCTION_ROLES)]
    ),
    context(
        RoutingPaths.cropEdit.route,
        "Edycja uprawy",
        "Zmiana danych wybranej uprawy.",
        "production",
        RoutingPaths.cropTable.route,
        [link("Lista upraw", RoutingPaths.cropTable.route, READ_PRODUCTION_ROLES)]
    ),
    context(
        RoutingPaths.cropDetails.route,
        "Szczegóły uprawy",
        "Widok szczegółowy uprawy.",
        "production",
        RoutingPaths.cropTable.route,
        [link("Lista upraw", RoutingPaths.cropTable.route, READ_PRODUCTION_ROLES)]
    ),
    context(
        RoutingPaths.strainTable.route,
        "Szczepy",
        "Lista szczepów używanych w produktach i procesach produkcyjnych.",
        "production",
        RoutingPaths.strainTable.route,
        [link("Dodaj szczep", RoutingPaths.strainAdd.route, MANAGE_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.strainAdd.route,
        "Nowy szczep",
        "Formularz nowego szczepu.",
        "production",
        RoutingPaths.strainTable.route,
        [link("Lista szczepów", RoutingPaths.strainTable.route, READ_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.strainEdit.route,
        "Edycja szczepu",
        "Zmiana danych wybranego szczepu.",
        "production",
        RoutingPaths.strainTable.route,
        [link("Lista szczep\u00f3w", RoutingPaths.strainTable.route, READ_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.strainDetails.route,
        "Szczegóły szczepu",
        "Widok informacyjny szczepu.",
        "production",
        RoutingPaths.strainTable.route,
        [link("Lista szczepów", RoutingPaths.strainTable.route, READ_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.contractorTable.route,
        "Kontrahenci",
        "Relacje z kontrahentami, spotkaniami i historią współpracy.",
        "relationships",
        RoutingPaths.contractorTable.route,
        [link("Dodaj kontrahenta", RoutingPaths.contractorAdd.route, MANAGE_CONTRACTORS_ROLES)]
    ),
    context(
        RoutingPaths.contractorAdd.route,
        "Nowy kontrahent",
        "Formularz nowego kontrahenta.",
        "relationships",
        RoutingPaths.contractorTable.route,
        [link("Lista kontrahentów", RoutingPaths.contractorTable.route, READ_CONTRACTORS_ROLES)]
    ),
    context(
        RoutingPaths.contractorEdit.route,
        "Edycja kontrahenta",
        "Zmiana danych kontrahenta.",
        "relationships",
        RoutingPaths.contractorTable.route,
        [link("Lista kontrahent\u00f3w", RoutingPaths.contractorTable.route, READ_CONTRACTORS_ROLES)]
    ),
    context(
        RoutingPaths.contractorDetails.route,
        "Szczegóły kontrahenta",
        "Jedno miejsce dla danych i historii kontrahenta.",
        "relationships",
        RoutingPaths.contractorTable.route,
        [link("Lista kontrahentów", RoutingPaths.contractorTable.route, READ_CONTRACTORS_ROLES)]
    ),
    context(
        RoutingPaths.contractorHistory.route,
        "Historia kontrahenta",
        "Historia zmian danych kontrahenta.",
        "relationships",
        RoutingPaths.contractorTable.route,
        [link("Lista kontrahentów", RoutingPaths.contractorTable.route, READ_CONTRACTORS_ROLES)]
    ),
    context(
        RoutingPaths.contractorOperationHistory.route,
        "Historia operacji kontrahenta",
        "Widok operacji powi\u0105zanych z kontrahentem.",
        "relationships",
        RoutingPaths.contractorTable.route,
        [link("Lista kontrahentów", RoutingPaths.contractorTable.route, READ_CONTRACTORS_ROLES)]
    ),
    context(
        RoutingPaths.userTable.route,
        "Użytkownicy",
        "Administracja kontami użytkowników i ich historią zmian.",
        "admin",
        RoutingPaths.userTable.route,
        [link("Dodaj użytkownika", RoutingPaths.userAdd.route, MANAGE_USERS_ROLES)]
    ),
    context(
        RoutingPaths.userAdd.route,
        "Nowy użytkownik",
        "Formularz nowego użytkownika.",
        "admin",
        RoutingPaths.userTable.route,
        [link("Lista użytkowników", RoutingPaths.userTable.route, MANAGE_USERS_ROLES)]
    ),
    context(
        RoutingPaths.userEdit.route,
        "Edycja użytkownika",
        "Zmiana danych użytkownika.",
        "admin",
        RoutingPaths.userTable.route,
        [link("Lista użytkowników", RoutingPaths.userTable.route, MANAGE_USERS_ROLES)]
    ),
    context(
        RoutingPaths.userDetails.route,
        "Historia użytkownika",
        "Szczegóły i historia zmian konkretnego użytkownika.",
        "admin",
        RoutingPaths.userTable.route,
        [link("Lista użytkowników", RoutingPaths.userTable.route, MANAGE_USERS_ROLES)]
    ),
    context(
        RoutingPaths.deploy.route,
        "Publikacja",
        "Podgląd projektów Cloudflare i statusów publikacji sklepu.",
        "admin",
        RoutingPaths.deploy.route
    ),
    context(
        RoutingPaths.unitOfMeasureTable.route,
        "Jednostki miary",
        "Zarządzanie jednostkami bazowymi i pomocniczymi.",
        "admin",
        RoutingPaths.unitOfMeasureTable.route,
        [link("Dodaj jednostkę", RoutingPaths.unitOfMeasureAdd.route, MANAGE_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.unitOfMeasureAdd.route,
        "Nowa jednostka miary",
        "Formularz nowej jednostki miary.",
        "admin",
        RoutingPaths.unitOfMeasureTable.route,
        [link("Lista jednostek", RoutingPaths.unitOfMeasureTable.route, MANAGE_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.unitOfMeasureEdit.route,
        "Edycja jednostki miary",
        "Zmiana danych jednostki miary.",
        "admin",
        RoutingPaths.unitOfMeasureTable.route,
        [link("Lista jednostek", RoutingPaths.unitOfMeasureTable.route, MANAGE_PRODUCTS_ROLES)]
    ),
    context(
        RoutingPaths.companyData.route,
        "Dane firmy",
        "Dane organizacji i ustawienia wspólne dla systemu.",
        "admin",
        RoutingPaths.companyData.route,
        [
            link("Edytuj dane firmy", RoutingPaths.companyDataEdit.route, MANAGE_SETTINGS_ROLES),
            link("Historia zmian", RoutingPaths.companyDataHistory.route, MANAGE_SETTINGS_ROLES)
        ]
    ),
    context(
        RoutingPaths.companyDataEdit.route,
        "Edycja danych firmy",
        "Zmiana danych organizacji i ustawień globalnych.",
        "admin",
        RoutingPaths.companyData.route,
        [link("Dane firmy", RoutingPaths.companyData.route, MANAGE_SETTINGS_ROLES)]
    ),
    context(
        RoutingPaths.companyDataHistory.route,
        "Historia danych firmy",
        "Widok historii zmian dla danych firmy.",
        "admin",
        RoutingPaths.companyData.route,
        [link("Dane firmy", RoutingPaths.companyData.route, MANAGE_SETTINGS_ROLES)]
    ),
    context(
        RoutingPaths.shopCategoryTable.route,
        "Kategorie sklepu",
        "Drzewo kategorii i powiązania wykorzystywane przez frontend sklepu.",
        "shop",
        RoutingPaths.shopCategoryTable.route
    ),
    context(
        RoutingPaths.shopDiscountCodeTable.route,
        "Kody rabatowe",
        "Zarządzanie kodami rabatowymi i ich ograniczeniami.",
        "shop",
        RoutingPaths.shopDiscountCodeTable.route
    ),
    context(
        RoutingPaths.shopBasketTable.route,
        "Koszyki sklepu",
        "Podgląd koszyków aktywnych w sklepie internetowym.",
        "shop",
        RoutingPaths.shopBasketTable.route
    ),
    context(
        RoutingPaths.shopBasketProductsTable.route,
        "Produkty w koszyku",
        "Szczegóły pozycji dodanych do wybranego koszyka.",
        "shop",
        RoutingPaths.shopBasketTable.route
    ),
    context(
        RoutingPaths.shopOrderTable.route,
        "Zamówienia sklepu",
        "Obsługa zamówień sklepowych, etapów realizacji i płatności.",
        "shop",
        RoutingPaths.shopOrderTable.route
    )
];

export function isAllowedForRoles(
    item: Pick<NavigationItemDefinition, "allowedForRoles"> | Pick<RouteActionDefinition, "allowedForRoles">,
    roles?: ApplicationUserRole[]
) {
    if (!item.allowedForRoles || item.allowedForRoles.length === 0) {
        return true;
    }

    return item.allowedForRoles.some(role => roles?.includes(role));
}

export function getAvailableNavigationItems(roles?: ApplicationUserRole[]) {
    return navigationItems.filter(item => isAllowedForRoles(item, roles));
}

export function getNavigationItemsBySection(roles?: ApplicationUserRole[]) {
    const availableItems = getAvailableNavigationItems(roles);

    return navigationSections.map(section => ({
        ...section,
        items: availableItems.filter(item => item.section === section.key)
    })).filter(section => section.items.length > 0);
}

export function getMobileNavigationItems(roles?: ApplicationUserRole[]) {
    const availableItems = getAvailableNavigationItems(roles);

    return {
        upper: availableItems.filter(item => item.mobilePlacement === "upper"),
        main: availableItems.filter(item => item.mobilePlacement === "main"),
        lower: availableItems.filter(item => item.mobilePlacement === "lower")
    };
}

export function getRouteContextByPattern(routePattern?: string) {
    if (!routePattern) {
        return undefined;
    }

    return routeContexts.find(contextItem => contextItem.routePattern === routePattern);
}

export function getSectionDefinition(sectionKey: NavigationSectionKey) {
    return navigationSections.find(section => section.key === sectionKey)!;
}

export function getNavigationItemByRoute(route?: string, roles?: ApplicationUserRole[]) {
    if (!route) {
        return undefined;
    }

    return getAvailableNavigationItems(roles).find(item => item.route === route);
}

export function getRelatedNavigationItems(
    sectionKey: NavigationSectionKey,
    roles?: ApplicationUserRole[],
    excludedRoute?: string
) {
    return getAvailableNavigationItems(roles)
        .filter(item => item.section === sectionKey && item.route !== excludedRoute)
        .slice(0, 4);
}

export function getVisibleActions(
    contextItem: RouteContextDefinition,
    roles?: ApplicationUserRole[]
) {
    return (contextItem.actions ?? []).filter(action => isAllowedForRoles(action, roles));
}
